import { resolveFlag } from '../../services/launchDarkly';
import type { LDFlags } from '../../services/types';

import { readDevOverride } from './devOverride';

export const JACKPOT_FLAG_OVERRIDE_KEY = 'debug:crown_jackpot_splash_feature';

// The one LD key the whole jackpot feature hangs off, host and game-ui alike. Declared here rather
// than in JackpotStore so applyJackpotFlagOverride can reach it without importing the store.
export const JACKPOT_FLAG: LDFlags = 'crown_jackpot_splash_feature';

/**
 * The switch's three states. A string, not a boolean, so a maintenance window is distinguishable from
 * a deliberate kill on a dashboard.
 */
export const JACKPOT_FEATURE_STATES = ['on', 'off', 'maintenance'] as const;

export type JackpotFeatureState = (typeof JACKPOT_FEATURE_STATES)[number];

const isJackpotFeatureState = (value: unknown): value is JackpotFeatureState =>
  typeof value === 'string' && (JACKPOT_FEATURE_STATES as readonly string[]).includes(value);

/**
 * Whether the surface is LIVE — only `on`. `maintenance` is not live: no request is made, no popup
 * opens, and the only thing rendered is the small widget's maintenance plate (see
 * {@link isJackpotFeatureInMaintenance}). Fails closed on anything else: an unset flag, a typo, or the
 * boolean the old `crown_jackpot_feature` key used to serve.
 */
export const isJackpotFeatureOn = (value: unknown): boolean => value === 'on';

/**
 * Whether the surface is in a maintenance window.
 *
 * Distinct from off BECAUSE the lobby shows something: the small widget renders with an "In
 * Maintenance" plate instead of a pot (design `10929:24559`, the `Disabled` variant of `Widget -
 * Small`), so a player who saw a jackpot yesterday is told it is coming back rather than finding the
 * row silently gone. Nothing else appears — no wide widget, no category row, no popup.
 */
export const isJackpotFeatureInMaintenance = (value: unknown): boolean => value === 'maintenance';

/**
 * The raw state, for the one caller that has to forward the LD VALUE rather than a decision — see
 * {@link applyJackpotFlagOverride}.
 */
export function resolveJackpotFlagState(flag: LDFlags): JackpotFeatureState {
  const override = readDevOverride(JACKPOT_FLAG_OVERRIDE_KEY);

  if (isJackpotFeatureState(override)) {
    return override;
  }

  const value = resolveFlag(flag, 'off' as never);

  return isJackpotFeatureState(value) ? value : 'off';
}

/**
 * LaunchDarkly owns `crown_jackpot_splash_feature`, and the fallback is 'off' — so before the flag
 * exists in the LD project the store makes no request at all and there is nothing to look at locally.
 * This is the dev-only escape hatch:
 *
 *   localStorage.setItem('debug:crown_jackpot_splash_feature', 'on')           // force on
 *   localStorage.setItem('debug:crown_jackpot_splash_feature', 'off')          // force off
 *   localStorage.setItem('debug:crown_jackpot_splash_feature', 'maintenance')  // small widget only
 *   localStorage.removeItem('debug:crown_jackpot_splash_feature')              // back to LD
 *
 * Fail-closed: ignored outside a local/dev build, ignored for any value that is not one of the three
 * states, and ignored when storage throws. Every other path defers to LD with the 'off' fallback.
 */
export function resolveJackpotFlag(flag: LDFlags): boolean {
  return isJackpotFeatureOn(resolveJackpotFlagState(flag));
}

/** Companion to {@link resolveJackpotFlag} for the maintenance branch. */
export function resolveJackpotMaintenance(flag: LDFlags): boolean {
  return isJackpotFeatureInMaintenance(resolveJackpotFlagState(flag));
}

/**
 * Overlays the same dev override onto a raw LD flag bag before it is handed to the game-ui iframe.
 *
 * The lobby resolves the flag through resolveJackpotFlag above, but game-ui asks the host
 * for `ldClient.allFlags()` — the raw LD values, which the override never touched. While the flag
 * does not exist in the LD project yet, that split means the lobby widget renders (override on)
 * and the popup it opens reads the flag as off and immediately posts `closeGame`, so the dialog
 * blinks shut. Both sides must resolve the flag the same way.
 *
 * No-op in production: readDevOverride returns null outside a local/dev build.
 */
export function applyJackpotFlagOverride<T extends Record<string, unknown>>(flags: T): T {
  const override = readDevOverride(JACKPOT_FLAG_OVERRIDE_KEY);

  if (!isJackpotFeatureState(override)) {
    return flags;
  }

  // The STATE, not a boolean: game-ui reads this bag as the raw LD value and maps it with its own
  // `isJackpotFeatureOn`. Handing it a boolean here would make the iframe read the flag as off.
  return { ...flags, [JACKPOT_FLAG]: override };
}
